<template>
  <el-card shadow="hover" class="menu-mgmt">
    <template slot="header">
      <div class="card-header">
        <span class="title">菜单管理</span>
        <div>
          <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate(null)">新增一级菜单</el-button>
          <el-button size="small" type="warning" plain icon="el-icon-key" @click="openRolePerm">角色功能权限</el-button>
          <el-button size="small" plain icon="el-icon-refresh" @click="loadMenus">刷新</el-button>
        </div>
      </div>
    </template>

    <el-table
      :data="menuTree"
      row-key="id"
      default-expand-all
      :tree-props="{ children: 'children' }"
      :indent="24"
      stripe
      border
      size="small"
      class="menu-table">
      <el-table-column label="菜单名称" prop="title" min-width="220"></el-table-column>
      <el-table-column label="图标" width="80" align="center">
        <template slot-scope="{ row }">
          <i v-if="row.icon" :class="row.icon" class="row-icon"></i>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="排序" prop="sort" width="80" align="center"></el-table-column>
      <el-table-column label="权限标识" min-width="220">
        <template slot-scope="{ row }">
          <template v-if="row.roleCodes">
            <el-tag v-for="r in row.roleCodes.split(',').map(s => s.trim()).filter(Boolean)" :key="r" size="mini" :type="roleTagType(r)" style="margin-right:4px">{{ roleLabel(r) }}</el-tag>
          </template>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="组件路径" prop="path" min-width="200"></el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.enabled === 1 ? 'primary' : 'info'" effect="plain">
            {{ row.enabled === 1 ? '正常' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170" align="center">
        <template slot-scope="{ row }">{{ formatTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="240" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" icon="el-icon-edit" @click="openEdit(row)">修改</el-button>
          <el-button v-if="!row.parentId" type="text" icon="el-icon-plus" @click="openCreate(row)">新增</el-button>
          <el-button type="text" icon="el-icon-delete" class="del-btn" @click="deleteRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px" :close-on-click-modal="false" :close-on-press-escape="false">
      <el-form :model="form" :rules="formRules" ref="formRef" label-width="100px" size="small">
        <el-form-item label="上级菜单">
          <el-select v-model="form.parentId" :disabled="dialogMode === 'edit' && hasOwnChildren(form.id)" placeholder="（一级菜单）" clearable :style="{ width: '100%' }">
            <el-option :value="null" label="（一级菜单）"></el-option>
            <el-option v-for="p in rootOptions" :key="p.id" :label="p.title" :value="p.id" :disabled="form.id === p.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="菜单名称" prop="title">
          <el-input v-model="form.title" placeholder="请输入菜单名称"></el-input>
        </el-form-item>
        <el-form-item label="组件路径" prop="path">
          <el-input v-model="form.path" :placeholder="form.parentId ? '请输入组件路径，例如：/user' : '一级菜单可留空（仅作为分组）'"></el-input>
        </el-form-item>
        <el-form-item label="图标">
          <el-input v-model="form.icon" placeholder="例如：el-icon-setting">
            <template slot="prepend"><i :class="form.icon"></i></template>
          </el-input>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :step="10" :style="{ width: '100%' }"></el-input-number>
        </el-form-item>
        <el-form-item label="权限标识">
          <el-checkbox-group v-model="form._roles">
            <el-checkbox v-for="r in roleOptions" :key="r" :label="r">{{ roleLabel(r) }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form._enabled" active-text="正常" inactive-text="停用"></el-switch>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 角色功能权限：按角色配置可操作的按钮权限，账号按角色继承 -->
    <el-dialog title="角色功能权限" :visible.sync="rolePermVisible" width="720px" :close-on-click-modal="false">
      <div style="margin-bottom:12px;">
        <span style="margin-right:8px;color:#606266;">选择角色：</span>
        <el-radio-group v-model="rolePermRoleId" @change="loadRoleFuncCodes">
          <el-radio-button :label="1">超级管理员</el-radio-button>
          <el-radio-button :label="2">行政管理员</el-radio-button>
          <el-radio-button :label="3">业务员</el-radio-button>
        </el-radio-group>
      </div>
      <el-alert v-if="rolePermRoleId === 1" type="info" :closable="false" show-icon
                title="超级管理员默认拥有全部权限，不受此处限制（可不勾）。"></el-alert>
      <p class="hint" style="color:#909399;font-size:13px;margin:10px 0;">勾选该角色在后台可执行的操作（该角色下所有账号自动继承）：</p>
      <div v-for="g in funcGroups" :key="g.module" style="margin-bottom:14px;">
        <div style="font-weight:600;color:#303133;margin-bottom:6px;border-left:3px solid #E6A23C;padding-left:8px;">{{ g.module }}</div>
        <el-checkbox-group v-model="funcSelected">
          <el-checkbox v-for="it in g.items" :key="it.code" :label="it.code" border style="margin:6px 8px 6px 0">{{ it.name }}</el-checkbox>
        </el-checkbox-group>
      </div>
      <div slot="footer">
        <el-button @click="rolePermVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingRolePerm" @click="saveRolePerm">保存</el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import { menuApi, roleApi, userApi } from '../api/api'

export default {
  data() {
    return {
      menus: [],
      roleOptions: ['super_admin', 'admin', 'salesman'],
      dialogVisible: false,
      dialogMode: 'create',
      submitting: false,
      form: this.emptyForm(),
      // 角色功能权限
      rolePermVisible: false,
      rolePermRoleId: 2,
      funcGroups: [],
      funcSelected: [],
      savingRolePerm: false
    }
  },
  computed: {
    menuTree() {
      const byId = {}
      const sorted = [...this.menus].sort((a, b) => (a.sort || 0) - (b.sort || 0))
      sorted.forEach(m => { byId[m.id] = { ...m }; byId[m.id].children = [] })
      const roots = []
      sorted.forEach(m => {
        const node = byId[m.id]
        if (m.parentId && byId[m.parentId]) {
          byId[m.parentId].children.push(node)
        } else {
          roots.push(node)
        }
      })
      roots.forEach(r => { if (!r.children.length) delete r.children })
      return roots
    },
    rootOptions() {
      return this.menus.filter(m => !m.parentId).sort((a, b) => (a.sort || 0) - (b.sort || 0))
    },
    dialogTitle() {
      if (this.dialogMode === 'edit') return '修改菜单'
      if (this.form.parentId) return '新增二级菜单'
      return '新增一级菜单'
    },
    formRules() {
      // 二级菜单（有 parentId）必填组件路径；一级菜单允许为空
      const required = !!this.form.parentId
      return {
        title: [{ required: true, message: '请输入菜单名称', trigger: 'blur' }],
        path: required
          ? [{ required: true, message: '请输入组件路径', trigger: 'blur' }]
          : []
      }
    }
  },
  mounted() { this.loadMenus() },
  methods: {
    openRolePerm() {
      this.rolePermVisible = true
      if (!this.funcGroups.length) {
        userApi.getFuncPermissions().then(res => {
          const list = res.data || []
          const trialCodes = [
            { code: 'func:trial:consumption', name: '标记试用消费情况', module: '产品试用' },
            { code: 'func:trial:unlock-limit', name: '解除产品试用上限限制', module: '产品试用' }
          ]
          trialCodes.forEach(tc => {
            if (!list.some(x => x.code === tc.code)) {
              list.push(tc)
            }
          })
          const byModule = []
          const idx = {}
          list.forEach(it => {
            if (idx[it.module] == null) { idx[it.module] = byModule.length; byModule.push({ module: it.module, items: [] }) }
            byModule[idx[it.module]].items.push({ code: it.code, name: it.name })
          })
          this.funcGroups = byModule
          this.loadRoleFuncCodes()
        })
      } else {
        this.loadRoleFuncCodes()
      }
    },
    loadRoleFuncCodes() {
      roleApi.getRoleFuncCodes(this.rolePermRoleId).then(res => { this.funcSelected = res.data || [] })
    },
    saveRolePerm() {
      this.savingRolePerm = true
      roleApi.saveRoleFuncCodes(this.rolePermRoleId, this.funcSelected).then(res => {
        if (res.data && res.data.success !== false) {
          this.$message.success('保存成功（该角色账号需重新登录后生效）')
          this.rolePermVisible = false
        } else {
          this.$message.error((res.data && res.data.message) || '保存失败')
        }
      }).finally(() => { this.savingRolePerm = false })
    },
    roleLabel(code) {
      const map = { 'super_admin': '超级管理员', 'admin': '行政管理员', 'salesman': '业务员' }
      return map[code] || code
    },
    roleTagType(code) {
      return code === 'super_admin' ? 'danger' : code === 'admin' ? 'warning' : 'success'
    },
    emptyForm() {
      return { id: null, parentId: null, title: '', path: '', icon: '', sort: 0, _roles: [], _enabled: true }
    },
    loadMenus() {
      menuApi.getAll().then(res => {
        this.menus = Array.isArray(res.data) ? res.data : []
      })
    },
    formatTime(t) {
      if (!t) return ''
      return String(t).replace('T', ' ').slice(0, 19)
    },
    hasOwnChildren(id) {
      return this.menus.some(m => m.parentId === id)
    },
    openCreate(parent) {
      this.dialogMode = 'create'
      this.form = this.emptyForm()
      if (parent) this.form.parentId = parent.id
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        parentId: row.parentId || null,
        title: row.title || '',
        path: row.path || '',
        icon: row.icon || '',
        sort: row.sort || 0,
        _roles: (row.roleCodes || '').split(',').map(s => s.trim()).filter(Boolean),
        _enabled: row.enabled === 1
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    submitForm() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        const payload = {
          id: this.form.id,
          title: this.form.title.trim(),
          path: (this.form.path || '').trim(),
          icon: this.form.icon || '',
          parentId: this.form.parentId || 0,
          roleCodes: this.form._roles.join(','),
          sort: this.form.sort || 0,
          enabled: this.form._enabled ? 1 : 0
        }
        this.submitting = true
        const req = this.dialogMode === 'edit' ? menuApi.updateMenu(payload) : menuApi.createMenu(payload)
        req.then(res => {
          if (res.data && res.data.success !== false) {
            this.$message.success(this.dialogMode === 'edit' ? '修改成功' : '新增成功')
            this.dialogVisible = false
            this.loadMenus()
            this.$root.$emit('menus-updated')
          } else {
            this.$message.error((res.data && res.data.message) || '操作失败')
          }
        }).finally(() => { this.submitting = false })
      })
    },
    deleteRow(row) {
      this.$confirm(`确认删除菜单「${row.title}」？`, '提示', { type: 'warning' }).then(() => {
        menuApi.deleteMenu(row.id).then(res => {
          if (res.data && res.data.success) {
            this.$message.success('删除成功')
            this.loadMenus()
            this.$root.$emit('menus-updated')
          } else {
            this.$message.error((res.data && res.data.message) || '删除失败')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.menu-mgmt .card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.menu-mgmt .title { font-weight: 600; font-size: 15px; }
.menu-table .row-icon { color: #606266; font-size: 16px; }
.menu-table .muted { color: #c0c4cc; }
.menu-table >>> .el-table__row .el-button--text { padding: 0 6px; }
.menu-table .del-btn { color: #F56C6C; }
.menu-table >>> .el-table__row .el-table__expand-icon { color: #909399; }
</style>
